import { Role } from '~/utils/shared/roles'
import { InferNative, Type } from '.'

export type HttpTrigger = {
  trigger: 'http'
  method: 'GET' | 'POST'
  route: `/${string}`
  validateOrigin: boolean
  server?: 'serverless' | 'main' // default is serverless
  manualResponse?: boolean
  requestFormat?: 'text' | 'json' | 'fileUpload'
}

export type WebsocketTrigger = {
  trigger: 'websocket'
  route: string
}

export type ChangeTrigger = {
  trigger: 'change'
}

export type CronTrigger = {
  trigger: 'cron'
  expression: string
}

export type LocalCallTrigger = {
  trigger: 'localCall'
}

export type Trigger =
  | HttpTrigger
  | WebsocketTrigger
  | ChangeTrigger
  | CronTrigger
  | LocalCallTrigger

export type Copy = { from: string; to: string }

export type BaseTrigger =
  | {
      trigger: string
    }
  | Trigger
export interface BaseActionDefinition {
  type: string
  key: string
  description: string
  runtime: {
    platform: string
    memory: number
    minInstances?: number
    maxInstances?: number
    long?: {
      machineType?: string
      storage?: number
      fileShareServers?: {
        serverName: string
      }[]
      attachedDisks?: string[]
      linuxDependencies?: string[]
      extraCommands?: string[]
    }
    extraLibraries?: string[]
    copy?: Copy[]
    timeout?: number
  }
  triggers: BaseTrigger[]
  access?: string[] | string
  restrictedToRoles?: Role[] | string[]
  userActivityLog?: boolean
}

export interface ActionDefinition<
  PlatformRuntime extends 'python39' | 'nodejs18' | 'nodejs20' | 'nodejs22' | 'python312' =
    | 'python39'
    | 'nodejs18'
    | 'nodejs20'
    | 'nodejs22'
    | 'python312',
> extends BaseActionDefinition {
  type: 'action'
  key: string
  description: string
  triggers: Trigger[]
  runtime: {
    extraLibraries?: string[]
    copy?: Copy[]
    timeout?: number
    minInstances?: number
    maxInstances?: number
    platform: PlatformRuntime
    memory: number
    long?: {
      machineType?: string
      storage?: number
      fileShareServers?: {
        serverName: string
      }[]
      attachedDisks?: string[]
      linuxDependencies?: string[]
      extraCommands?: string[]
      customDockerfile?: boolean
    }
  }
  access?:
    | ('public' | 'private' | 'internal' | 'service')[]
    | 'public'
    | 'private'
    | 'internal'
    | 'service'
  restrictedToRoles?: Role[]
}

/**
 * Configurações de cache, com possíveis configurações para cache frontal (front) e cache traseiro (back).
 *
 * @param parameters Os parâmetros usados para configurar o cache.
 * @returns Configuração do cache para a query.
 */
export interface Cache {
  front?: {
    /**
     * Tempo, em milissegundos, após o qual uma atualização é necessária. Antes desse tempo, os dados serão resgatados do cache front.
     */
    expirationMsForUpdate?: number

    /**
     * Em caso de falha ao resgatar dados, ele define por quanto tempo os dados serão utilizados ao invés de retornar erro, em milissegundos.
     */
    expirationMsForFallback?: number

    /**
     * Intervalo, em milissegundos, para atualização automática da tela resgatando novos dados e apresentando. Garante que telas paradas por muito tempo sejam atualizadas com dados mais recentes.
     */
    autoUpdateMs?: number

    // /**
    //  * Quantidade máxima de itens que podem ser armazenados em memória rápida.
    //  */
    // memoizationMaxSize?: number

    // /**
    //  * Tempo, em milissegundos, para os dados em memória rápida serem descartados.
    //  */
    // memoizationMaxAgeMs?: number
  }
  back?: {
    /**
     * Tempo, em milissegundos, após o qual uma atualização é necessária. Antes desse tempo, os dados serão resgatados do cache.
     */
    expirationMsForUpdate?: number

    /**
     * Em caso de falha ao resgatar dados, ele define por quanto tempo os dados serão utilizados ao invés de retornar erro, em milissegundos.
     */
    expirationMsForFallback?: number

    // /**
    //  * Tempo, em milissegundos, para manter estes mesmos dados disponíveis para todos em cache CDN. ⚠️ CUIDADO, ELE MANTÉM OS DADOS DISPONÍVEIS PARA TODOS, MESMO QUE SEJAM DADOS SENSÍVEIS OU PRIVADOS.
    //  */
    // publicCdnMs?: number

    // /**
    //  * Quantidade máxima de itens que podem ser armazenados em memória rápida.
    //  */
    // memoizationMaxSize?: number

    // /**
    //  * Tempo, em milissegundos, para os dados em memória rápida serem descartados.
    //  */
    // memoizationMaxAgeMs?: number
  }
}

export interface BaseActionDefinitionWithInputOutput<
  InputType extends Type,
  OutputType extends Type,
> extends BaseActionDefinition {
  input: InputType
  output: OutputType
  operation?: 'create' | 'read' | 'update' | 'delete'
  targetEntity?: string
}

export interface BaseActionDefinitionEnhanced<InputType extends Type, OutputType extends Type>
  extends BaseActionDefinition {
  input: InputType
  output: OutputType
  cache?: (input: InferNative<InputType>) => Cache
}

export interface ActionDefinitionWithInputOutput<InputType extends Type, OutputType extends Type>
  extends ActionDefinition {
  input: InputType
  output: OutputType
  operation?: 'create' | 'read' | 'update' | 'delete'
  targetEntity?: string
}

export interface ActionDefinitionEnhanced<InputType extends Type, OutputType extends Type>
  extends ActionDefinition {
  input: InputType
  output: OutputType
  cache?: (input: InferNative<InputType>) => Cache
}

export default function defineAction<
  GenericBaseActionDefinitionEnhanced extends BaseActionDefinitionEnhanced<Type, Type>,
>(actionDefinition: GenericBaseActionDefinitionEnhanced): GenericBaseActionDefinitionEnhanced {
  return actionDefinition
}
